import React, { useState } from 'react';
import { Edit2, Trash2, Settings, ChevronDown, ChevronUp } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const WorkflowCanvas = ({ nodes, onUpdateNode, onDeleteNode, triggerType }) => {
  const [expandedNodes, setExpandedNodes] = useState({});
  const [editingNode, setEditingNode] = useState(null);

  const toggleNodeExpansion = (nodeId) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev?.[nodeId]
    }));
  };

  const handleEditNode = (node) => {
    setEditingNode(node?.id);
    setExpandedNodes(prev => ({ ...prev, [node?.id]: true }));
  };

  const handleSaveNode = (nodeId) => {
    setEditingNode(null);
  };

  const handleConfigChange = (nodeId, configKey, value) => {
    const node = nodes?.find(n => n?.id === nodeId);
    onUpdateNode(nodeId, {
      config: {
        ...node?.config,
        [configKey]: value
      }
    });
  };

  const renderNodeConfig = (node) => {
    const isEditing = editingNode === node?.id;
    const isExpanded = expandedNodes?.[node?.id];

    if (!isExpanded) return null;

    switch (node?.type) {
      case 'trigger':
        return (
          <div className="space-y-3 mt-3 pt-3 border-t border-gray-200">
            <Input
              label="Confidence Threshold"
              type="number"
              min="0"
              max="1"
              step="0.1"
              value={node?.config?.conditions?.confidenceThreshold || 0.7}
              onChange={(e) => handleConfigChange(node?.id, 'conditions', {
                ...node?.config?.conditions,
                confidenceThreshold: parseFloat(e?.target?.value)
              })}
              disabled={!isEditing}
            />
            <Select
              label="Violation Type"
              value={node?.config?.conditions?.violationType || 'organic_certification'}
              onChange={(e) => handleConfigChange(node?.id, 'conditions', {
                ...node?.config?.conditions,
                violationType: e?.target?.value
              })}
              disabled={!isEditing}
              options={[
                { value: 'organic_certification', label: 'Organic Certification' },
                { value: 'pesticide_limit', label: 'Pesticide Limit' },
                { value: 'application_timing', label: 'Application Timing' },
                { value: 'record_keeping', label: 'Record Keeping' }
              ]}
            />
          </div>
        );

      case 'sms_alert':
        return (
          <div className="space-y-3 mt-3 pt-3 border-t border-gray-200">
            <Select
              label="Recipient Role"
              value={node?.config?.recipientRole || 'supervisor'}
              onChange={(e) => handleConfigChange(node?.id, 'recipientRole', e?.target?.value)}
              disabled={!isEditing}
              options={[
                { value: 'supervisor', label: 'Supervisor' },
                { value: 'manager', label: 'Manager' },
                { value: 'field_technician', label: 'Field Technician' },
                { value: 'compliance_officer', label: 'Compliance Officer' }
              ]}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <textarea
                value={node?.config?.message || ''}
                onChange={(e) => handleConfigChange(node?.id, 'message', e?.target?.value)}
                disabled={!isEditing}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50"
              />
            </div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={node?.config?.includeDetails !== false}
                onChange={(e) => handleConfigChange(node?.id, 'includeDetails', e?.target?.checked)}
                disabled={!isEditing}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <span className="text-sm text-gray-700">Include alert details</span>
            </label>
          </div>
        );

      case 'task_assignment':
        return (
          <div className="space-y-3 mt-3 pt-3 border-t border-gray-200">
            <Input
              label="Task Title"
              value={node?.config?.taskTitle || ''}
              onChange={(e) => handleConfigChange(node?.id, 'taskTitle', e?.target?.value)}
              disabled={!isEditing}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Task Description</label>
              <textarea
                value={node?.config?.taskDescription || ''}
                onChange={(e) => handleConfigChange(node?.id, 'taskDescription', e?.target?.value)}
                disabled={!isEditing}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50"
              />
            </div>
            <Select
              label="Assign To Role"
              value={node?.config?.assignToRole || 'field_technician'}
              onChange={(e) => handleConfigChange(node?.id, 'assignToRole', e?.target?.value)}
              disabled={!isEditing}
              options={[
                { value: 'field_technician', label: 'Field Technician' },
                { value: 'supervisor', label: 'Supervisor' },
                { value: 'compliance_officer', label: 'Compliance Officer' }
              ]}
            />
            <Select
              label="Priority"
              value={node?.config?.priority || 'high'}
              onChange={(e) => handleConfigChange(node?.id, 'priority', e?.target?.value)}
              disabled={!isEditing}
              options={[
                { value: 'low', label: 'Low' },
                { value: 'medium', label: 'Medium' },
                { value: 'high', label: 'High' },
                { value: 'critical', label: 'Critical' }
              ]}
            />
            <Input
              label="Due In (Hours)"
              type="number"
              value={node?.config?.dueInHours || 24}
              onChange={(e) => handleConfigChange(node?.id, 'dueInHours', parseInt(e?.target?.value))}
              disabled={!isEditing}
            />
          </div>
        );

      case 'escalation':
        return (
          <div className="space-y-3 mt-3 pt-3 border-t border-gray-200">
            <Select
              label="Escalate To Role"
              value={node?.config?.escalateToRole || 'manager'}
              onChange={(e) => handleConfigChange(node?.id, 'escalateToRole', e?.target?.value)}
              disabled={!isEditing}
              options={[
                { value: 'supervisor', label: 'Supervisor' },
                { value: 'manager', label: 'Manager' },
                { value: 'company_admin', label: 'Company Admin' }
              ]}
            />
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={node?.config?.notifyViaSms !== false}
                onChange={(e) => handleConfigChange(node?.id, 'notifyViaSms', e?.target?.checked)}
                disabled={!isEditing}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <span className="text-sm text-gray-700">Notify via SMS</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={node?.config?.notifyViaEmail !== false}
                onChange={(e) => handleConfigChange(node?.id, 'notifyViaEmail', e?.target?.checked)}
                disabled={!isEditing}
                className="rounded border-gray-300 text-green-600 focus:ring-green-500"
              />
              <span className="text-sm text-gray-700">Notify via Email</span>
            </label>
          </div>
        );

      case 'email_notification':
        return (
          <div className="space-y-3 mt-3 pt-3 border-t border-gray-200">
            <Select
              label="Recipient Role"
              value={node?.config?.recipientRole || 'supervisor'}
              onChange={(e) => handleConfigChange(node?.id, 'recipientRole', e?.target?.value)}
              disabled={!isEditing}
              options={[
                { value: 'supervisor', label: 'Supervisor' },
                { value: 'manager', label: 'Manager' },
                { value: 'compliance_officer', label: 'Compliance Officer' }
              ]}
            />
            <Input
              label="Subject"
              value={node?.config?.subject || ''}
              onChange={(e) => handleConfigChange(node?.id, 'subject', e?.target?.value)}
              disabled={!isEditing}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
              <textarea
                value={node?.config?.message || ''}
                onChange={(e) => handleConfigChange(node?.id, 'message', e?.target?.value)}
                disabled={!isEditing}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-50"
              />
            </div>
          </div>
        );

      case 'delay':
        return (
          <div className="space-y-3 mt-3 pt-3 border-t border-gray-200">
            <Input
              label="Delay (Minutes)"
              type="number"
              value={node?.config?.delayMinutes || 30}
              onChange={(e) => handleConfigChange(node?.id, 'delayMinutes', parseInt(e?.target?.value))}
              disabled={!isEditing}
            />
          </div>
        );

      case 'condition':
        return (
          <div className="space-y-3 mt-3 pt-3 border-t border-gray-200">
            <Select
              label="Condition Type"
              value={node?.config?.conditionType || 'confidence_score'}
              onChange={(e) => handleConfigChange(node?.id, 'conditionType', e?.target?.value)}
              disabled={!isEditing}
              options={[
                { value: 'confidence_score', label: 'Confidence Score' },
                { value: 'severity_level', label: 'Severity Level' },
                { value: 'time_of_day', label: 'Time of Day' }
              ]}
            />
            <Select
              label="Operator"
              value={node?.config?.operator || 'greater_than'}
              onChange={(e) => handleConfigChange(node?.id, 'operator', e?.target?.value)}
              disabled={!isEditing}
              options={[
                { value: 'greater_than', label: 'Greater Than' },
                { value: 'less_than', label: 'Less Than' },
                { value: 'equals', label: 'Equals' },
                { value: 'greater_or_equal', label: 'Greater or Equal' }
              ]}
            />
            <Input
              label="Value"
              type="number"
              step="0.1"
              value={node?.config?.value || 0.8}
              onChange={(e) => handleConfigChange(node?.id, 'value', parseFloat(e?.target?.value))}
              disabled={!isEditing}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Workflow Canvas</h2>
        <span className="text-sm text-gray-500">{nodes?.length} node(s)</span>
      </div>

      {nodes?.length === 0 ? (
        <div className="text-center py-12">
          <Settings className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No nodes added yet</p>
          <p className="text-sm text-gray-500 mt-1">
            Add triggers and actions from the side panels to build your workflow
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {nodes?.map((node, index) => {
            const IconComponent = node?.icon;
            const isExpanded = expandedNodes?.[node?.id];
            const isEditing = editingNode === node?.id;

            return (
              <div key={node?.id}>
                <div className={`p-4 rounded-lg border-2 transition-all ${
                  isExpanded ? 'border-green-500 bg-green-50' : 'border-gray-200'
                }`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`p-2 rounded-lg ${node?.bgColor}`}>
                        <IconComponent className={`w-5 h-5 ${node?.color}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-gray-900">{node?.label}</h3>
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded">
                            {node?.type}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">
                          Node {index + 1} of {nodes?.length}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => toggleNodeExpansion(node?.id)}
                        className="p-1 hover:bg-gray-100 rounded"
                        title={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-600" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-600" />
                        )}
                      </button>
                      {isExpanded && (
                        <>
                          <button
                            onClick={() => isEditing ? handleSaveNode(node?.id) : handleEditNode(node)}
                            className="p-1 hover:bg-gray-100 rounded"
                            title={isEditing ? 'Save' : 'Edit'}
                          >
                            <Edit2 className="w-4 h-4 text-blue-600" />
                          </button>
                          <button
                            onClick={() => onDeleteNode(node?.id)}
                            className="p-1 hover:bg-gray-100 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {renderNodeConfig(node)}

                  {isExpanded && isEditing && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <Button
                        onClick={() => handleSaveNode(node?.id)}
                        size="sm"
                        className="w-full"
                      >
                        Save Configuration
                      </Button>
                    </div>
                  )}
                </div>

                {index < nodes?.length - 1 && (
                  <div className="flex justify-center py-2">
                    <div className="w-0.5 h-6 bg-gray-300"></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {nodes?.length > 0 && (
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-medium text-gray-900 mb-2">Workflow Execution Flow</h3>
          <p className="text-sm text-gray-600">
            This workflow will execute {nodes?.length} node(s) in sequence when triggered.
            Click on each node to configure its settings.
          </p>
        </div>
      )}
    </div>
  );
};

export default WorkflowCanvas;